/* React base class */
import { Component } from "react"

/* Redux store connection */
import { connect } from "react-redux"

/* QR code renderer */
import { QRCodeSVG } from "qrcode.react"

/* Router HOC */
import { withRouter, applySeo } from "../../helpers"

/* Shared UI components */
import { Account, Loading, Network } from "../../components"

/* Icon component */
import Icon from "../../components/Icon"

/* Certificate purchase modal */
import CertificateModal from "../../components/CertificateModal"

/* Certificate API: list of received/sent certificates */
import { list } from "../../api/Certificate"

/* i18n */
import { translate } from "../../translates"

/* Certificate gradient themes */
const THEMES = {
    uni: { gradient: "linear-gradient(135deg, #0a84ff, #6f5bff)" },
    her: { gradient: "linear-gradient(135deg, #ff5fa2, #ff9a6b)" },
    him: { gradient: "linear-gradient(135deg, #0f1729, #3b4a63)" },
    spa: { gradient: "linear-gradient(135deg, #18a999, #5fd0c0)" },
    sunset: { gradient: "linear-gradient(135deg, #ff6a00, #ee0979)" },
    night: { gradient: "linear-gradient(135deg, #360033, #7b2ff7)" }
}

/* Status labels and note text keyed by certificate status */
const STATUS_LABELS = {
    active: translate("Активен"),
    used: translate("Использован"),
    expired: translate("Истёк"),
    pending: translate("Ожидает оплаты"),
    failed: translate("Оплата не прошла")
}

const STATUS_NOTES = {
    used: "Сертификат уже использован",
    expired: "Срок действия истёк",
    pending: "Ожидает оплаты",
    failed: "Оплата не прошла"
}

const MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"]

/* Page Gifts */
class Gifts extends Component {

    constructor(props) {
        super(props)

        /* State */
        this.state = {
            certOpen: false,
            tab: "received",
            loadingReceived: true,
            loadingSent: true,
            received: [],
            sent: [],
            errorReceived: false,
            errorSent: false,
            paymentNotice: null,
            detail: null,
            toast: null
        }
    }

    componentDidMount = () => {

        /* SEO */
        applySeo({ title: `Подарочные сертификаты Bookify`, description: `Дарите подарочные сертификаты Bookify — их принимают в салонах красоты, барбершопах и сервисах по всему Казахстану.`, keywords: `подарочный сертификат, сертификат в салон красоты, подарок, Bookify`, path: `/gifts` })

        /* Scroll */
        window.scroll(0, 0)

        /* Read the payment result from the query when we return from the gateway */
        const params = new URLSearchParams(this.props.location ? this.props.location.search : "")
        const payment = params.get("payment")
        if (payment === "success") {
            this.setState({ paymentNotice: { type: "success", text: "Оплата прошла — сертификат активирован!" } })
        }
        else if (payment === "fail") {
            this.setState({ paymentNotice: { type: "fail", text: "Оплата не прошла. Сертификат не оформлен." } })
        }

        /* Clean the query so a refresh doesn't repeat the notice */
        if (payment) {
            this.props.navigate("/gifts", { replace: true })
        }

        /* Load */
        this._load()
    }

    /* Draw Load */
    _load = () => {

        /* Fields */
        const { token } = this.props

        /* Guests have no certificates */
        if (!token) {
            this.setState({ loadingReceived: false, loadingSent: false })
            return
        }

        /* Fetch received and sent lists in parallel */
        this.setState({ loadingReceived: true, loadingSent: true, errorReceived: false, errorSent: false })
        list(token, "received").then((response) => {
            if (response && response.status === 200) {
                this.setState({ received: Array.isArray(response.data) ? response.data : [], loadingReceived: false })
            }
            else {
                this.setState({ loadingReceived: false, errorReceived: true })
            }
        })
        list(token, "sent").then((response) => {
            if (response && response.status === 200) {
                this.setState({ sent: Array.isArray(response.data) ? response.data : [], loadingSent: false })
            }
            else {
                this.setState({ loadingSent: false, errorSent: true })
            }
        })
    }

    /* Draw Toast */
    _toast = (message) => {

        /* Show a short auto-hiding message */
        this.setState({ toast: message })
        setTimeout(() => this.setState({ toast: null }), 2600)
    }

    /* Draw Open Create */
    _openCreate = () => {

        /* Fields */
        const { token, toggleAuth } = this.props

        /* Require sign in before buying a certificate */
        if (!token) {
            toggleAuth(true)
            return
        }
        this.setState({ certOpen: true })
    }

    /* Draw Copy Code */
    _copyCode = (code) => {

        /* Copy the code through a throwaway textarea */
        const textarea = document.createElement("textarea")
        textarea.value = code
        textarea.style.position = "fixed"
        textarea.style.opacity = "0"
        document.body.appendChild(textarea)
        textarea.select()
        try {
            document.execCommand("copy")
        }
        catch (error) {
            /* silent */
        }
        document.body.removeChild(textarea)
        this._toast("Код скопирован")
    }

    /* Draw Money */
    _money = (value) => `${Number(value || 0).toLocaleString("ru-RU")} ₸`

    /* Draw Format Date */
    _formatDate = (dateString) => {

        /* Empty or invalid dates fall back to the raw string */
        if (!dateString) {
            return ""
        }
        const date = new Date(dateString)
        if (isNaN(date.getTime())) {
            return dateString
        }
        return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`
    }

    /* Draw Certificate Card */
    _certCard = (certificate, index) => {

        /* Fields */
        const { tab } = this.state
        const themeKey = certificate.theme && THEMES[certificate.theme] ? certificate.theme : "uni"
        const gradient = THEMES[themeKey].gradient
        const status = certificate.status || "active"
        const isActive = status === "active"
        const showQr = isActive && tab === "received"
        const balance = certificate.balance != null ? certificate.balance : certificate.amount
        const partiallyUsed = balance < certificate.amount
        const statusLabel = STATUS_LABELS[status] || STATUS_LABELS.active
        const note = isActive ? (certificate.expiresAt ? `Действует до ${this._formatDate(certificate.expiresAt)}` : "") : STATUS_NOTES[status]

        return (
            <div key={`${certificate.id || index}`} className={`gifts__card${showQr ? " gifts__card--clickable" : ""}`} onClick={showQr ? () => this.setState({ detail: certificate }) : undefined}>
                <div className="cert gifts__cert" style={{ background: gradient }}>
                    <div className="cert__shine" />
                    <div className="cert__top">
                        <span className="cert__brand">bookify</span>
                        <span className="cert__chip">Подарочный сертификат</span>
                    </div>
                    <div className="cert__amount">{this._money(certificate.amount)}</div>
                    <div className="cert__foot">
                        <div>
                            <div className="cert__label">Кому</div>
                            <div className="cert__val">{certificate.recipient || "—"}</div>
                        </div>
                        <div>
                            <div className="cert__label">Код</div>
                            <div className="cert__val">{certificate.code || "—"}</div>
                        </div>
                    </div>
                </div>

                <div className="gifts__body">
                    <div className="gifts__status-row">
                        <div className={`gifts__status gifts__status--${status}`}>
                            <span className="gifts__dot" />
                            <span>{statusLabel}</span>
                        </div>
                        <span className="gifts__code">{certificate.code}</span>
                    </div>
                    <div className="gifts__rows">
                        {certificate.sender && (
                            <div className="gifts__row">
                                <span className="gifts__row-label">{translate("От кого")}</span>
                                <span className="gifts__row-val">{certificate.sender}</span>
                            </div>
                        )}
                        <div className="gifts__row">
                            <span className="gifts__row-label">{translate("Номинал")}</span>
                            <span className="gifts__row-val">{this._money(certificate.amount)}</span>
                        </div>
                        {certificate.expiresAt && (
                            <div className="gifts__row">
                                <span className="gifts__row-label">{translate("Действует до")}</span>
                                <span className="gifts__row-val">{this._formatDate(certificate.expiresAt)}</span>
                            </div>
                        )}
                    </div>
                    {partiallyUsed && isActive && (
                        <div className="gifts__remain">Остаток: {this._money(balance)} из {this._money(certificate.amount)}</div>
                    )}
                    {note && (
                        <div className={`gifts__note${isActive ? "" : " gifts__note--warn"}`}>{note}</div>
                    )}
                    {showQr && (
                        <button className="btn gifts__action" onClick={() => this.setState({ detail: certificate })}>
                            <Icon name="qr" size={15} />
                            {translate("Показать QR-код")}
                        </button>
                    )}
                    {tab === "sent" && (
                        <button className="btn btn--ghost gifts__action" onClick={this._openCreate}>
                            <Icon name="plus" size={14} />
                            {translate("Подарить ещё")}
                        </button>
                    )}
                </div>
            </div>
        )
    }

    /* Draw QR Modal */
    _qrModal = () => {

        /* Fields */
        const { detail } = this.state

        /* Nothing selected */
        if (!detail) {
            return null
        }

        /* Derived values */
        const themeKey = detail.theme && THEMES[detail.theme] ? detail.theme : "uni"
        const gradient = THEMES[themeKey].gradient
        const balance = detail.balance != null ? detail.balance : detail.amount
        const partiallyUsed = balance < detail.amount
        const rows = [
            { label: translate("Кому"), value: detail.recipient },
            detail.sender ? { label: translate("От кого"), value: detail.sender } : null,
            { label: translate("Номинал"), value: this._money(detail.amount) },
            partiallyUsed ? { label: translate("Остаток"), value: this._money(balance) } : null,
            detail.expiresAt ? { label: translate("Действует до"), value: this._formatDate(detail.expiresAt) } : null
        ].filter(Boolean)

        return (
            <div className="modal-backdrop" onClick={() => this.setState({ detail: null })}>
                <div className="modal modal--sm" onClick={(event) => event.stopPropagation()}>
                    <div className="modal__header">
                        <h3 className="modal__title">{translate("Сертификат")}</h3>
                        <button className="modal__close" aria-label="Закрыть" onClick={() => this.setState({ detail: null })}><Icon name="close" size={20} /></button>
                    </div>
                    <div className="modal__body">
                        <div className="cert" style={{ background: gradient }}>
                            <div className="cert__shine" />
                            <div className="cert__top">
                                <span className="cert__brand">bookify</span>
                                <span className="cert__chip">Подарочный сертификат</span>
                            </div>
                            <div className="cert__amount">{this._money(balance)}</div>
                            {partiallyUsed && (
                                <div className="cert__label gifts__cert-remain">Остаток из номинала {this._money(detail.amount)}</div>
                            )}
                        </div>

                        <p className="gifts__qr-hint">{translate("Покажите QR-код мастеру для активации сертификата")}</p>

                        <div className="gifts__qr-wrap">
                            <div className="gifts__qr-box">
                                <QRCodeSVG value={detail.code} size={200} level="H" fgColor="#0f1729" />
                                <div className="gifts__qr-badge" style={{ background: gradient }}>
                                    <Icon name="tag" size={22} />
                                </div>
                            </div>
                        </div>

                        <div className="gifts__coderow">
                            <div>
                                <div className="gifts__code-label">{translate("Код сертификата")}</div>
                                <div className="gifts__code-val">{detail.code}</div>
                            </div>
                            <button className="btn btn--ghost gifts__copy" onClick={() => this._copyCode(detail.code)}>
                                <Icon name="share" size={14} /> {translate("Копировать")}
                            </button>
                        </div>

                        <div className="gifts__detail">
                            {rows.map((row) => (
                                <div key={row.label} className="gifts__row">
                                    <span className="gifts__row-label">{row.label}</span>
                                    <span className="gifts__row-val">{row.value}</span>
                                </div>
                            ))}
                        </div>

                        {detail.message && (
                            <div className="gifts__message">
                                <span className="gifts__message-icon"><Icon name="chat" size={16} /></span>
                                <span className="gifts__message-text">{detail.message}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    /* Draw Tab Content */
    _tabContent = () => {

        /* Fields */
        const { tab, loadingReceived, loadingSent, received, sent, errorReceived, errorSent } = this.state
        const loading = tab === "received" ? loadingReceived : loadingSent
        const error = tab === "received" ? errorReceived : errorSent
        const items = tab === "received" ? received : sent

        /* Loading */
        if (loading) {
            return <Loading />
        }

        /* Error */
        if (error) {
            return <Network onRetry={this._load} />
        }

        /* Empty */
        if (!items.length) {
            return (
                <div className="empty">
                    <div className="empty__art"><Icon name="tag" size={48} /></div>
                    <h3>{tab === "received" ? translate("Сертификатов пока нет") : translate("Вы ещё не дарили сертификаты")}</h3>
                    <p>{tab === "received" ? translate("Здесь появятся подарочные сертификаты, которые вам отправили.") : translate("Подарите близким заботу о себе — сертификат принимается во всех салонах Bookify.")}</p>
                    {tab === "sent" && (
                        <button className="btn btn--lg gifts__empty-btn" onClick={this._openCreate}>
                            <Icon name="plus" size={15} />
                            Подарить сертификат
                        </button>
                    )}
                </div>
            )
        }

        /* List */
        return (
            <div className="gifts__grid">
                {items.map((certificate, index) => this._certCard(certificate, index))}
            </div>
        )
    }

    render = () => {

        /* Fields */
        const { certOpen, tab, received, sent, paymentNotice, toast } = this.state

        return (
            <Account active="gifts">
                <div className="content">
                    {paymentNotice && (
                        <div className={`gifts__notice gifts__notice--${paymentNotice.type === "success" ? "success" : "fail"}`}>
                            <Icon name={paymentNotice.type === "success" ? "check" : "close"} size={18} />
                            <span className="gifts__notice-text">{paymentNotice.text}</span>
                        </div>
                    )}
                    <div className="gifts__head">
                        <h2 className="content__title gifts__title">{translate("Сертификаты")}</h2>
                        <button className="btn btn--lg gifts__head-btn" onClick={this._openCreate}>
                            <Icon name="plus" size={16} />
                            Подарить сертификат
                        </button>
                    </div>
                    <p className="content__subtitle">{translate("Подарочные сертификаты, полученные и отправленные вами")}</p>

                    <div className="tabs">
                        <button className={`tab${tab === "received" ? " is-active" : ""}`} onClick={() => this.setState({ tab: "received" })}>
                            {translate("Мои сертификаты")}{received.length > 0 ? ` · ${received.length}` : ""}
                        </button>
                        <button className={`tab${tab === "sent" ? " is-active" : ""}`} onClick={() => this.setState({ tab: "sent" })}>
                            {translate("Я подарил")}{sent.length > 0 ? ` · ${sent.length}` : ""}
                        </button>
                    </div>

                    {this._tabContent()}
                </div>

                {certOpen && (
                    <CertificateModal onClose={() => this.setState({ certOpen: false }, this._load)} />
                )}

                {this._qrModal()}

                {toast && (
                    <div className="toast">
                        <div className="toast__check"><Icon name="check" size={16} /></div>
                        <div>{toast}</div>
                    </div>
                )}
            </Account>
        )
    }
}

/* Map State To Props */
const mapStateToProps = state => {
    return {
        token: state.token,
        user: state.user,
        lang: state.lang
    }
}

/* Map Dispatch To Props */
const mapDispatchToProps = dispatch => {
    return {
        toggleAuth: data => dispatch({ type: "TOGGLE_AUTH", payload: data })
    }
}

/* Export */
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Gifts))
